/**
 * Vista del PROYECTO FINAL (M2) — hito capstone de la cohorte.
 *
 * ALUMNO (el payoff): ve la rúbrica PUBLICADA antes de entregar (criterios,
 * niveles y puntos), la fecha límite con cuenta regresiva, entrega texto/archivos
 * (reusa el mismo camino que el módulo assignment) y, tras la corrección, la nota
 * puntuada por rúbrica + feedback + veredicto de aprobación (server-authoritative).
 *
 * MANAGER (autor/lead/ManageAcademics): designa QUÉ assignment es el proyecto
 * final, publica su rúbrica, activa la corrección de un solo intento y fija la
 * fecha límite de la cohorte. Todo server-authoritative + activity-log.
 */
import { type Component, createResource, createSignal, For, Show, untrack } from "solid-js";
import { msg } from "@lingui/core/macro";
import { CheckCircle2, Clock, FileText, Paperclip, Trophy } from "lucide-solid";
import { submitAssignmentMutation } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { showToast } from "@/components/ui/toast";
import { Skeleton } from "@/components/ui/skeleton";
import { uploadSubmissionFile } from "@/features/academy/assessment/api";
import {
  fetchFinalProject,
  type FinalProjectView as FinalProjectDTO,
  saveDeadline,
  saveDesignation,
} from "./api";
import { Countdown } from "./countdown";
import { RubricPreview } from "./rubric-preview";

const field =
  "w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-accent";

const pad2 = (n: number): string => String(n).padStart(2, "0");

function toDatetimeLocal(iso: string | null): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  // Formato técnico yyyy-MM-ddThh:mm para <input type="datetime-local"> (no es texto de UI).
  // eslint-disable-next-line lingui/no-unlocalized-strings
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export const FinalProjectView: Component<{ cohortId: string }> = (props) => {
  const { t } = useI18n();
  const { userId } = useSession();
  const drizzle = useDrizzle();

  const [view, { refetch }] = createResource(
    () => props.cohortId,
    async (id) => fetchFinalProject(id),
  );

  return (
    <Show when={!view.loading} fallback={<Skeleton class="h-64 w-full rounded-2xl" />}>
      <Show
        when={view()}
        fallback={
          <p class="text-muted-foreground" data-testid="final-project-error">
            {t(msg`This final project is not available.`)}
          </p>
        }
      >
        {(v) => (
          <div class="space-y-5" data-testid="final-project-view">
            <Show when={v().is_manager}>
              <ManagerPanel view={v()} onSaved={() => void refetch()} />
            </Show>

            <Show
              when={v().assignment}
              fallback={
                <div
                  class="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
                  data-testid="final-project-none"
                >
                  {v().is_manager
                    ? t(msg`Designate an assignment above to activate the final project.`)
                    : t(msg`No final project has been set for this cohort yet.`)}
                </div>
              }
            >
              {(a) => (
                <div
                  class="space-y-4 rounded-2xl border border-border bg-surface p-4 md:p-5"
                  data-testid="final-project-card"
                >
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <div class="flex items-center gap-2">
                      <Trophy class="h-5 w-5 text-accent" />
                      <h2
                        class="font-display text-lg font-extrabold"
                        data-testid="final-project-title"
                      >
                        {a().title}
                      </h2>
                    </div>
                    <Show when={v().deadline}>
                      <Countdown deadlineISO={v().deadline!} serverNowISO={v().server_now} />
                    </Show>
                  </div>

                  <Show when={a().instructions}>
                    <p class="whitespace-pre-wrap text-sm text-muted-foreground">
                      {a().instructions}
                    </p>
                  </Show>

                  {/* Veredicto de completion */}
                  <Show when={v().passed !== null}>
                    <span
                      class={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
                        v().passed
                          ? "bg-joy-mint/15 text-joy-mint"
                          : "bg-muted text-muted-foreground"
                      }`}
                      data-testid="final-project-passed"
                      data-passed={v().passed ? "true" : "false"}
                    >
                      <CheckCircle2 class="h-3.5 w-3.5" />
                      {v().passed ? t(msg`Final project passed`) : t(msg`Not yet passed`)}
                    </span>
                  </Show>

                  {/* Rúbrica: publicada ⇒ visible; el manager siempre la ve */}
                  <Show
                    when={a().rubric.content}
                    fallback={
                      <p
                        class="text-sm text-muted-foreground"
                        data-testid="final-project-rubric-hidden"
                      >
                        {t(msg`The rubric will be shared before you submit.`)}
                      </p>
                    }
                  >
                    <Show when={v().is_manager && !a().rubric.published}>
                      <p
                        class="text-xs font-medium text-warning"
                        data-testid="final-project-rubric-draft"
                      >
                        {t(msg`Draft — not yet visible to students.`)}
                      </p>
                    </Show>
                    <RubricPreview
                      rubric={a().rubric.content}
                      rubricScores={v().my_grade?.rubric_scores}
                    />
                  </Show>

                  {/* Nota devuelta */}
                  <Show
                    when={
                      v().my_grade &&
                      (v().my_grade!.status === "graded" || v().my_grade!.status === "returned")
                    }
                  >
                    <div class="rounded-xl bg-joy-mint/15 p-3" data-testid="final-project-grade">
                      <div class="flex items-center gap-1.5 text-sm font-bold text-joy-mint">
                        <CheckCircle2 class="h-4 w-4" />
                        {t(msg`Grade`)}: {v().my_grade!.score ?? "—"} / {a().max_score}
                      </div>
                      <Show when={v().my_grade!.feedback}>
                        <p
                          class="mt-1 whitespace-pre-wrap text-sm"
                          data-testid="final-project-feedback"
                        >
                          {v().my_grade!.feedback}
                        </p>
                      </Show>
                    </div>
                  </Show>

                  {/* Entrega (alumno) */}
                  <Show when={!v().is_manager}>
                    <SubmissionArea
                      view={v()}
                      assignmentMaxScore={a().max_score}
                      onSaved={() => void refetch()}
                      drizzleReady={Boolean(drizzle())}
                      submitBody={(body) => {
                        // Handler de evento (click): las lecturas reactivas se hacen
                        // síncronamente aquí; el trabajo async se delega a una función
                        // local que sólo usa valores ya capturados (no es tracked scope).
                        const db = drizzle();
                        const uid = userId();
                        const snap = v();
                        const a2 = snap.assignment;
                        if (!db || !uid || !a2) {
                          return Promise.resolve();
                        }
                        const run = async (): Promise<void> => {
                          await submitAssignmentMutation(db, {
                            assignmentId: a2.id,
                            cohortId: snap.cohort_id,
                            organizationId: snap.organization_id,
                            userId: uid,
                            body,
                            files: snap.my_submission?.files ?? [],
                          });
                          showToast(t(msg`Submitted`), "success");
                          void refetch();
                        };
                        return run();
                      }}
                      uploadFile={(file) => {
                        const uid = userId();
                        const snap = v();
                        const a2 = snap.assignment;
                        if (!uid || !a2) {
                          return Promise.resolve();
                        }
                        const cohortId = snap.cohort_id;
                        const run = async (): Promise<void> => {
                          try {
                            await uploadSubmissionFile(a2.id, uid, cohortId, file);
                            showToast(t(msg`File uploaded`), "success");
                            void refetch();
                          } catch {
                            showToast(t(msg`Upload failed`), "error");
                          }
                        };
                        return run();
                      }}
                    />
                  </Show>
                </div>
              )}
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};

// ── Entrega del alumno ──────────────────────────────────────────────────────────

const SubmissionArea: Component<{
  view: FinalProjectDTO;
  assignmentMaxScore: number;
  drizzleReady: boolean;
  submitBody: (body: string) => Promise<void>;
  uploadFile: (file: File) => Promise<void>;
  onSaved: () => void;
}> = (props) => {
  const { t } = useI18n();
  const a = () => props.view.assignment!;
  const grade = () => props.view.my_grade;
  // Corrección única: una vez devuelta la nota, no se reenvía.
  const locked = () =>
    a().single_correction &&
    Boolean(grade()) &&
    (grade()!.status === "returned" || grade()!.status === "graded");
  // Semilla de una sola vez (campo editable): `untrack` documenta que ignoramos a
  // propósito cambios posteriores del prop para no pisar lo que escribe el alumno.
  const [body, setBody] = createSignal(untrack(() => props.view.my_submission?.body ?? ""));

  return (
    <div class="space-y-2 border-t border-border pt-3" data-testid="final-project-submission">
      <Show when={props.view.my_submission?.late}>
        <div
          class="flex items-center gap-1 text-xs font-bold text-danger"
          data-testid="final-project-late"
        >
          <Clock class="h-3 w-3" /> {t(msg`Submitted late`)}
        </div>
      </Show>

      <Show
        when={!locked()}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="final-project-locked">
            {t(msg`Single correction: this project can no longer be resubmitted.`)}
          </p>
        }
      >
        <Show when={a().submission_type === "text"}>
          <textarea
            class={field}
            rows={5}
            data-testid="final-project-submit-text"
            value={body()}
            onInput={(e) => setBody(e.currentTarget.value)}
            placeholder={t(msg`Write your final project…`)}
          />
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground disabled:opacity-50"
            data-testid="final-project-submit-button"
            disabled={!props.drizzleReady}
            onClick={() => void props.submitBody(body())}
          >
            <FileText class="h-4 w-4" />
            {props.view.my_submission ? t(msg`Update submission`) : t(msg`Submit project`)}
          </button>
        </Show>

        <Show when={a().submission_type === "file"}>
          <label class="inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground">
            <Paperclip class="h-4 w-4" />
            {t(msg`Upload file`)}
            <input
              type="file"
              class="hidden"
              data-testid="final-project-submit-file"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) {
                  void props.uploadFile(f);
                }
              }}
            />
          </label>
        </Show>
      </Show>

      <Show when={(props.view.my_submission?.files.length ?? 0) > 0}>
        <ul class="text-xs text-muted-foreground" data-testid="final-project-files">
          <For each={props.view.my_submission?.files ?? []}>{(k) => <li>{k}</li>}</For>
        </ul>
      </Show>
    </div>
  );
};

// ── Panel del manager (designación + rúbrica + fecha límite) ─────────────────────

const ManagerPanel: Component<{ view: FinalProjectDTO; onSaved: () => void }> = (props) => {
  const { t } = useI18n();
  const current = () => props.view.candidates.find((c) => c.is_final_project);

  const designate = async (
    assignmentId: string,
    patch: { is_final_project?: boolean; rubric_published?: boolean; single_correction?: boolean },
  ) => {
    const a = props.view.assignment;
    const base = {
      is_final_project: current()?.id === assignmentId,
      rubric_published: a?.rubric.published ?? false,
      single_correction: a?.single_correction ?? false,
    };
    try {
      await saveDesignation(assignmentId, { ...base, ...patch });
      showToast(t(msg`Saved`), "success");
      props.onSaved();
    } catch {
      showToast(t(msg`Could not save. A course can have only one final project.`), "error");
    }
  };

  // Semilla de una sola vez del campo editable (ver nota en SubmissionArea).
  const [deadline, setDeadline] = createSignal(untrack(() => toDatetimeLocal(props.view.deadline)));
  const persistDeadline = async () => {
    const raw = deadline();
    const iso = raw ? new Date(raw).toISOString() : null;
    try {
      await saveDeadline(props.view.cohort_id, iso);
      showToast(t(msg`Deadline saved`), "success");
      props.onSaved();
    } catch {
      showToast(t(msg`Could not save the deadline.`), "error");
    }
  };

  return (
    <div
      class="space-y-4 rounded-2xl border border-accent/30 bg-accent/5 p-4"
      data-testid="final-project-manager"
    >
      <h3 class="font-display text-sm font-extrabold uppercase tracking-wide text-accent">
        {t(msg`Final project setup`)}
      </h3>

      <div class="space-y-2">
        <For
          each={props.view.candidates}
          fallback={
            <p class="text-sm text-muted-foreground">
              {t(msg`Create an assignment in this course to designate it as the final project.`)}
            </p>
          }
        >
          {(c) => (
            <div
              class="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-surface p-3"
              data-testid="final-project-candidate"
              data-designated={c.is_final_project ? "true" : "false"}
            >
              <div class="min-w-0">
                <span class="font-medium">{c.title}</span>
                <span class="ml-2 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                  {c.kind}
                </span>
              </div>
              <Show
                when={c.is_final_project}
                fallback={
                  <button
                    type="button"
                    class="rounded-lg bg-accent px-3 py-1.5 text-xs font-bold text-accent-foreground"
                    data-testid="final-project-designate"
                    onClick={() => void designate(c.id, { is_final_project: true })}
                  >
                    {t(msg`Set as final project`)}
                  </button>
                }
              >
                <button
                  type="button"
                  class="rounded-lg border border-border px-3 py-1.5 text-xs font-bold text-muted-foreground"
                  data-testid="final-project-undesignate"
                  onClick={() =>
                    void designate(c.id, {
                      is_final_project: false,
                      rubric_published: false,
                      single_correction: false,
                    })
                  }
                >
                  {t(msg`Remove`)}
                </button>
              </Show>
            </div>
          )}
        </For>
      </div>

      {/* Ajustes del proyecto designado */}
      <Show when={props.view.assignment}>
        {(a) => (
          <div class="space-y-3 border-t border-accent/20 pt-3">
            <label class="flex items-center justify-between gap-2 text-sm">
              <span>
                <span class="font-semibold">{t(msg`Publish rubric to students`)}</span>
                <span class="block text-xs text-muted-foreground">
                  {t(msg`Students see the rubric before submitting.`)}
                </span>
              </span>
              <input
                type="checkbox"
                class="h-4 w-4"
                data-testid="final-project-publish-rubric"
                checked={a().rubric.published}
                onChange={(e) =>
                  void designate(a().id, { rubric_published: e.currentTarget.checked })
                }
              />
            </label>

            <label class="flex items-center justify-between gap-2 text-sm">
              <span>
                <span class="font-semibold">{t(msg`Single correction`)}</span>
                <span class="block text-xs text-muted-foreground">
                  {t(msg`One shot: no resubmission after the correction.`)}
                </span>
              </span>
              <input
                type="checkbox"
                class="h-4 w-4"
                data-testid="final-project-single-correction"
                checked={a().single_correction}
                onChange={(e) =>
                  void designate(a().id, { single_correction: e.currentTarget.checked })
                }
              />
            </label>

            <div class="flex flex-wrap items-end gap-2">
              <label class="text-sm">
                <span class="block font-semibold">{t(msg`Deadline (this cohort)`)}</span>
                <input
                  type="datetime-local"
                  class={field}
                  data-testid="final-project-deadline-input"
                  value={deadline()}
                  onInput={(e) => setDeadline(e.currentTarget.value)}
                />
              </label>
              <button
                type="button"
                class="rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground"
                data-testid="final-project-deadline-save"
                onClick={() => void persistDeadline()}
              >
                {t(msg`Save deadline`)}
              </button>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
};
